import { Tel } from "#app/_ui/components/media/Tel";
import { hoursOnlineSupport, telOnlineSupport } from "#app/lib/company";
import { toUsaTelHref } from "#app/lib/telephone";
import {
  AlertCallout,
  AlertContent,
  AlertTitle,
  type AlertCalloutProps,
} from "#ui/alert";
import { LinkButton } from "#ui/link";

export function SignInAlertCallout({
  code,
  message,
  ...props
}: Omit<AlertCalloutProps, "children" | "intent" | "role"> & {
  code?: number;
  message?: string | null;
}) {
  // Session Destroyed
  if (code === 1) {
    return (
      <SignInAlertCalloutContent {...props} intent="warning">
        You have successfully signed out of Baird Online.
      </SignInAlertCalloutContent>
    );
  }

  // Session Expired
  if (code === 2) {
    return (
      <SignInAlertCalloutContent {...props} intent="warning">
        Your session has expired.
      </SignInAlertCalloutContent>
    );
  }

  // Service Enabled with message OR Route Enabled with message
  if (code === 3 && message) {
    return (
      <SignInAlertCalloutContent {...props} intent="warning">
        {message}
      </SignInAlertCalloutContent>
    );
  }

  // Service Disabled with/without message OR Route Disabled with/without message
  if (code === 4) {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        <>
          {message
            ? message
            : "Sign in is currently unavailable while we perform system maintenance."}
          <br />
          <br />
          We apologize in advance for any inconvenience that this may cause and
          thank you for your patience and understanding. We hope to have the
          problem resolved shortly, please try again later or contact{" "}
          <LinkButton
            href="/tech-support"
            variant="link"
            palette="error"
            underline
          >
            Baird Online Support
          </LinkButton>{" "}
          or call{" "}
          <Tel href={telOnlineSupport} hrefFn={toUsaTelHref}>
            {telOnlineSupport}
          </Tel>
          , {hoursOnlineSupport} for assistance.
        </>
      </SignInAlertCalloutContent>
    );
  }

  // Authorized Blocked
  if (code === 5) {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        Your request has been blocked and you have been logged out of the
        system.
      </SignInAlertCalloutContent>
    );
  }

  // Unauthorized Blocked
  if (code === 6 || code === 451) {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        Your request has been blocked.
      </SignInAlertCalloutContent>
    );
  }

  // Authorized
  if (code === 200) {
    return (
      <SignInAlertCalloutContent {...props} intent="success">
        Sign in successful, redirecting...
      </SignInAlertCalloutContent>
    );
  }

  // Forbidden Unlocked OR Unauthorized OR Not Found
  if ((code === 403 && message !== "Locked") || code === 401 || code === 400) {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        We did not recognize the User ID and/or password. Remember that
        passwords are case sensitive. Please try again.
      </SignInAlertCalloutContent>
    );
  }

  // Forbidden Locked
  if (code === 403 && message === "Locked") {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        Sign in locked. To unlock, please reset your password via the forgot
        password link or contact Baird Online Support.
      </SignInAlertCalloutContent>
    );
  }

  // Service Unavailable (Maintenance Ongoing)
  if (code === 503) {
    return (
      <SignInAlertCalloutContent {...props} intent="error">
        You are not able to log in as we are performing system maintenance to
        address unexpected technical difficulties. We hope to have the problem
        resolved shortly, please try again later or contact Baird Online Support
        at{" "}
        <Tel href={telOnlineSupport} hrefFn={toUsaTelHref}>
          {telOnlineSupport}
        </Tel>{" "}
        if you are unable to login for an extended period of time.
      </SignInAlertCalloutContent>
    );
  }

  // TODO: Handle unhandled 4xx and 5xx errors with a generic message?
  return null;
}

function SignInAlertCalloutContent({
  children,
  intent,
  ...props
}: Omit<AlertCalloutProps, "intent" | "role"> & {
  children: React.ReactNode;
  intent: "error" | "warning" | "success";
}) {
  return (
    <AlertCallout
      {...props}
      intent={intent}
      // Intentional live announcements use role status.
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="status"
    >
      <AlertContent>
        <AlertTitle>{children}</AlertTitle>
      </AlertContent>
    </AlertCallout>
  );
}
